import type { ElementRef } from '@angular/core'
import { ChangeDetectionStrategy, Component, inject, input, output, viewChild } from '@angular/core'
import { NgOptimizedImage } from '@angular/common'
import { ToastService } from '../../../services/toast-service/toast.service'
import { FileDropValidationService } from '../../../services/file-drop-validation-service/file-drop-validation.service'

@Component({
  selector: 'aw-file-drop',
  templateUrl: './file-drop.component.html',
  styleUrls: ['./file-drop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgOptimizedImage]
})
export class FileDropComponent {
  private toastService = inject(ToastService)
  private fileDropValidationService = inject(FileDropValidationService)

  readonly emoji = input('🫳')
  readonly illustrationSrc = input<string>()
  readonly selectable = input(false)
  readonly disabled = input(false)
  readonly ariaLabel = input<string>()
  readonly onSelectFiles = output<void>()
  // INFO: This input controls whether the component allows pointer events or not.
  // There is a weird case on the drop zone of the task-detail that creates flickering
  // when the mouse is over the drop zone. When the content being projected needs to be
  // clickable, set this input to true.
  readonly allowPointerEvents = input(false)

  readonly onFilesDropped = output<File[]>()

  private readonly dropArea = viewChild.required<ElementRef<HTMLElement>>('dropArea')

  protected readonly translations = q.translations.FileDropComponent

  attachmentFiles: File[] = []
  uploadError = ''
  isDraggingOver = false

  /** Moves keyboard focus to the selectable drop area. */
  focus(): void {
    this.dropArea().nativeElement.focus()
  }

  /** Opens file selection when the drop area is selectable.
   * @param {Event} event - The activation event.
   */
  protected selectFiles(event: Event): void {
    if (!this.selectable() || this.disabled()) {
      return
    }

    event.preventDefault()
    this.onSelectFiles.emit()
  }

  /**
   * Handles a file drop when the drop area is enabled.
   * @param {DragEvent} event - The file drop event.
   */
  protected fileDropped(event: DragEvent): void {
    event.stopPropagation()
    event.preventDefault()

    this.isDraggingOver = false

    if (this.disabled()) {
      return
    }

    if (this.fileDropValidationService.rejectDirectories(event.dataTransfer)) {
      this.onFilesDropped.emit(null)

      return
    }

    this.attachmentFiles = Array.from(event.dataTransfer.files)

    const hasEmptyFiles = this.attachmentFiles?.some(file => !file.size)

    if (this.attachmentFiles.length && !hasEmptyFiles) {
      this.onFilesDropped.emit(this.attachmentFiles)
    } else {
      this.toastService.show(q.translations.FileContextMenu.fileWithNoSize, { type: 'error' })
      this.onFilesDropped.emit(null)
    }
  }

  /**
   * Highlights the enabled drop area during a file drag.
   * @param {DragEvent} event - The drag event over the drop area.
   */
  protected onDragOver(event: DragEvent): void {
    event.stopPropagation()
    event.preventDefault()

    this.isDraggingOver = !this.disabled()
  }
}
